/* Server state.
 *
 * Every read and every mutation goes through here, so no component ever holds
 * server data in its own useState — that is how two parts of a page end up
 * disagreeing about the same post.
 *
 * The interesting part is the optimistic like. A like must feel instant, but
 * the same post can be on screen in several cached pages at once (the home
 * feed and a church tab), and the server's count is the only one that is true.
 * `onMutate` writes the guess into every cache that holds that post,
 * `onError` puts back exactly what was there, and `onSuccess` overwrites the
 * guess with the server's number.
 */
import { useEffect, useRef } from 'react'
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type InfiniteData,
} from '@tanstack/react-query'
import { ApiError, api, tokens, uploadFile } from './client'
import { pushGranted } from './push'
import type {
  MyGroupsPage,
  LiveEvent,
  JoinRequestPerson,
  ChurchAwaitingMe,
  ChurchDetail,
  Feeling,
  HelpOption,
  Media,
  PrayerComment,
  ChurchStats,
  Comment,
  Member,
  Group,
  GroupDetail,
  GroupEvent,
  PermissionGroup,
  Poll,
  PollTally,
  ChurchRole,
  RsvpPerson,
  RsvpStatus,
  Voter,
  Invitation,
  InviteResult,
  MyInvitation,
  NotificationsPage,
  CreateCommentResult,
  EditResult,
  LikeResult,
  Me,
  Page,
  Post,
  PostType,
  PrayerRequest,
  ReactionKindInfo,
  Reactor,
  ReactResult,
  PrayerState,
  PrayerTab,
  RespondResult,
  ResponseType,
  UnreadCount,
  Visibility,
  AudienceKind,
  ChurchMessage,
  ChannelPreferences,
  ChurchMessagingAnalytics,
  Devotion,
  MessageAnalytics,
  MessageRecipient,
  MessageTemplate,
  CallAnalytics,
  AudienceField,
  AudienceFilter,
  AudiencePreview,
  CallAttendancePage,
  MemberTable,
  ScheduledCall,
  CallKind,
  RoomState,
  TemplatePreview,
  AttendanceToday,
  ChurchPlace,
  CheckedIn,
  FollowUpKind,
  FollowUpPage,
  Household,
  HouseholdMemberInput,
  MyAttendance,
  NumberedPage,
  Occurrence,
  OccurrenceReport,
  Roll,
  Service,
  ServiceInput,
  WeekItem,
} from '@/types/api'

/** Fetch a list endpoint and hand back just the rows.
 *
 * EVERY list in the API returns `{items, next_cursor, has_more}` — see
 * docs/API_CONVENTIONS.md. Most screens here show a complete, bounded list
 * (the roles in a church, the events in a group) and have no pager, so they
 * want the array and nothing else.
 *
 * Unwrapping HERE rather than in each component means the envelope change did
 * not touch a single screen, and the day one of these lists does need paging,
 * only its own hook changes — `useMembers` can start returning the cursor
 * without every other list learning about it.
 */
async function items<T>(path: string): Promise<T[]> {
  const page = await api.get<Page<T>>(path)
  return page.items
}

/** Query keys in one place. A typo in a key is invisible — it silently creates
 *  a second cache entry rather than failing — so they are never inlined. */
export const keys = {
  me: ['me'] as const,
  /** 'all' rather than an omitted segment: without it the Testimonies page
   *  and the home feed would share one cache entry, and whichever loaded
   *  second would overwrite the other's pages. */
  feed: (churchId?: string | null, postType?: string) =>
    ['feed', churchId ?? 'home', postType ?? 'all'] as const,
  comments: (postId: string) => ['comments', postId] as const,
  post: (postId: string) => ['post', postId] as const,
  /** 'all' rather than null: an undefined segment would collapse into the
   *  same key as the first kind's. */
  postReactions: (postId: string, kind: string | null) =>
    ['post', postId, 'reactions', kind ?? 'all'] as const,
  reactionKinds: ['reaction-kinds'] as const,
  prayerRequest: (id: string) => ['prayer-request', id] as const,
  prayer: (tab: PrayerTab, state?: PrayerState | null) =>
    ['prayer', tab, state ?? 'any'] as const,
  notifications: ['notifications'] as const,
  unreadCount: ['notifications', 'unread-count'] as const,
  church: (id: string) => ['church', id] as const,
  helpOptions: ['help', 'options'] as const,
  myOffers: ['help', 'offers'] as const,
  helpMatches: (kinds: string[]) => ['help', 'matches', ...kinds] as const,
  following: ['prayer', 'following'] as const,
  followedRequests: ['prayer', 'followed-requests'] as const,
  prayerComments: (id: string) => ['prayer', id, 'comments'] as const,
  churchStats: (id: string) => ['church', id, 'stats'] as const,
  members: (id: string) => ['church', id, 'members'] as const,
  groups: (id: string) => ['church', id, 'groups'] as const,
  // Not scoped by church id: the server derives it from the session, and a
  // person belongs to one church at a time.
  myGroups: ['my-groups'] as const,
  joinRequests: (cid: string, gid: string) =>
    ['church', cid, 'groups', gid, 'join-requests'] as const,
  groupFeed: (gid: string) => ['feed', 'group', gid] as const,
  group: (cid: string, gid: string) => ['church', cid, 'groups', gid] as const,
  groupMembers: (cid: string, gid: string) =>
    ['church', cid, 'groups', gid, 'members'] as const,
  groupInvitations: (cid: string, gid: string) =>
    ['church', cid, 'groups', gid, 'invitations'] as const,
  myInvitations: ['my-invitations'] as const,
  groupEvents: (cid: string, gid: string) =>
    ['church', cid, 'groups', gid, 'events'] as const,
  groupPolls: (cid: string, gid: string) =>
    ['church', cid, 'groups', gid, 'polls'] as const,
  roles: (cid: string) => ['church', cid, 'roles'] as const,
  permissionCatalogue: ['permission-catalogue'] as const,
  myPermissions: (cid: string) => ['church', cid, 'my-permissions'] as const,
  messages: (cid: string) => ['church', cid, 'messages'] as const,
  message: (cid: string, id: string) =>
    ['church', cid, 'messages', id] as const,
  channelPreferences: ['channel-preferences'] as const,
  messageRecipients: (cid: string, id: string, filters: string) =>
    ['church', cid, 'messages', id, 'recipients', filters] as const,
  messageAnalytics: (cid: string, id: string) =>
    ['church', cid, 'messages', id, 'analytics'] as const,
  messagingAnalytics: (cid: string) =>
    ['church', cid, 'messaging-analytics'] as const,
  devotions: (cid: string) => ['church', cid, 'devotions'] as const,
  calls: (cid: string, upcoming: boolean, gid?: string | null) =>
    ['church', cid, 'calls', upcoming, gid ?? 'all'] as const,
  callAnalytics: (cid: string, id: string) =>
    ['church', cid, 'calls', id, 'analytics'] as const,
  /* NOT keyed by church: one approved set serves every church. */
  messageTemplates: (channel?: string) =>
    ['message-templates', channel ?? 'all'] as const,
}

type FeedPages = InfiniteData<Page<Post>>
type PrayerPages = InfiniteData<Page<PrayerRequest>>

/* ---------------------------------------------------------------- session */

export function useMe() {
  return useQuery({
    queryKey: keys.me,
    queryFn: () => api.get<Me>('/auth/me'),
    // Only ask when there is a token to ask with; otherwise every signed-out
    // page load fires a request that can only 401.
    enabled: Boolean(tokens.access),
    // Identity rarely changes, and re-fetching it on every window focus is a
    // request per tab switch for no benefit.
    staleTime: 5 * 60_000,
    retry: false,
  })
}

/** What the caller's church has switched on, plus the groupings the
 *  navigation needs.
 *
 *  Everything reads as on until /auth/me answers, and when the caller has no
 *  church at all. The server refuses a switched-off action regardless, so the
 *  only cost of guessing "on" is a moment's visibility — whereas guessing
 *  "off" would flash entry points away and back on every load. */
export function useFeatures() {
  const { data: me } = useMe()
  const f = me?.features
  const on = (v: boolean | undefined) => v ?? true
  const features = {
    send_individual_messages: on(f?.send_individual_messages),
    send_bulk_msgs: on(f?.send_bulk_msgs),
    audio_conf_calls: on(f?.audio_conf_calls),
    video_conf_calls: on(f?.video_conf_calls),
    live_broadcasts: on(f?.live_broadcasts),
    events: on(f?.events),
    polls: on(f?.polls),
  }
  return {
    ...features,
    anyMessaging: features.send_individual_messages || features.send_bulk_msgs,
    anyCalls: features.audio_conf_calls || features.video_conf_calls,
    anyMeetings:
      features.audio_conf_calls ||
      features.video_conf_calls ||
      features.live_broadcasts,
  }
}

/* ------------------------------------------------------------------- feed */

export function useFeed(churchId?: string | null, postType?: PostType) {
  return useInfiniteQuery({
    queryKey: keys.feed(churchId, postType),
    queryFn: ({ pageParam }) => {
      const params = new URLSearchParams({ limit: '20' })
      if (pageParam) params.set('cursor', pageParam as string)
      if (churchId) params.set('church_id', churchId)
      if (postType) params.set('post_type', postType)
      return api.get<Page<Post>>(`/feed?${params}`)
    },
    initialPageParam: undefined as string | undefined,
    // Returning undefined is how TanStack learns there is no next page; the
    // server sends next_cursor: null on the last one.
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    staleTime: 30_000,
  })
}

export function useCreatePost() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: {
      body: string
      post_type?: PostType
      visibility?: Visibility
      church_id?: string | null
      group_id?: string | null
      // Ids from POST /media, uploaded while the composer was open.
      media_ids?: string[]
        /** A key from GET /feelings — "— feeling blessed". */
        feeling?: string | null
      /** Share My Testimony: the answered Prayer Request this testimony is
          the story of. Requires post_type 'testimony'. */
      prayer_request_id?: string | null
    }) => api.post<Post>('/posts', input),
    onSuccess: (post) => {
      // A group post also belongs on its group's wall, which is a separate
      // cache entry from the home feed.
      if (post.group_id) {
        qc.invalidateQueries({ queryKey: keys.groupFeed(post.group_id) })
      }

      // Put it straight at the top of the home feed rather than refetching:
      // a refetch would scroll-jump and discard pages already loaded. Safe for
      // a group post too — the author is in the group, so the server would
      // return it there anyway.
      qc.setQueryData<FeedPages>(keys.feed(null), (old) => {
        if (!old) return old
        const [first, ...rest] = old.pages
        return {
          ...old,
          pages: [{ ...first, items: [post, ...first.items] }, ...rest],
        }
      })
      // The church tab, if this went to one, is left to refetch — it may be
      // filtered or sorted differently and is usually not on screen.
      if (post.church_id) {
        qc.invalidateQueries({ queryKey: keys.feed(post.church_id) })
      }
      // A testimony also belongs on the Testimonies page, which is its own
      // cache entry. Refetched rather than prepended: it is not the screen
      // somebody is looking at when they share one, so there is no scroll
      // position to protect.
      if (post.post_type === 'testimony') {
        qc.invalidateQueries({ queryKey: keys.feed(null, 'testimony') })
      }
    },
  })
}

export function useDeletePost() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (postId: string) => api.delete<void>(`/posts/${postId}`),
    onSuccess: (_data, postId) => {
      // Remove from every cached feed rather than invalidating: the post is
      // gone, and refetching would show it again until the response lands.
      qc.setQueriesData<FeedPages>({ queryKey: ['feed'] }, (old) => {
        if (!old) return old
        return {
          ...old,
          pages: old.pages.map((p) => ({
            ...p,
            items: p.items.filter((post) => post.id !== postId),
          })),
        }
      })
    },
  })
}

/** Rewrite one post wherever it is cached.
 *
 *  A post is on screen in several places at once — the home feed, a church
 *  tab, a group's wall, and the single-post entry a notification pinned — and
 *  a reaction has to land in all of them or two parts of the page disagree
 *  about the same card. Patching each list separately is how that drift starts.
 */
function patchPost(
  qc: ReturnType<typeof useQueryClient>,
  postId: string,
  patch: (post: Post) => Post,
) {
  qc.setQueriesData<FeedPages>({ queryKey: ['feed'] }, (old) => {
    if (!old) return old
    return {
      ...old,
      pages: old.pages.map((page) => ({
        ...page,
        items: page.items.map((post) => (post.id === postId ? patch(post) : post)),
      })),
    }
  })
  // The pinned copy is its own cache entry, not a row in a feed page.
  qc.setQueryData<Post>(keys.post(postId), (old) => (old ? patch(old) : old))
}

/** Set, change or clear a reaction, optimistically.
 *
 *  THE PLAIN TAP IS THE 'like' KIND, so Like and the picker go down one path.
 *  Two paths would drift: tapping Like on a post already marked Pray has to
 *  replace the Pray, and only the reaction path knows that.
 *
 *  The rollback is what makes this safe: if the request fails the previous
 *  cache is restored exactly, so a failed reaction leaves no phantom count.
 */
export function useReactToPost() {
  const qc = useQueryClient()

  return useMutation({
    mutationFn: ({ postId, kind }: { postId: string; kind: string }) =>
      api.post<ReactResult>(`/posts/${postId}/react`, { kind }),

    onMutate: async ({ postId, kind }) => {
      // Stop any in-flight feed fetch from landing on top of the guess.
      await qc.cancelQueries({ queryKey: ['feed'] })
      const previous = [
        ...qc.getQueriesData<FeedPages>({ queryKey: ['feed'] }),
        [keys.post(postId), qc.getQueryData(keys.post(postId))] as const,
      ]

      patchPost(qc, postId, (post) => {
        // Tapping the kind you already chose clears it.
        const clearing = post.viewer_reaction === kind
        const counts = { ...post.reaction_counts }

        if (post.viewer_reaction) {
          const left = (counts[post.viewer_reaction] ?? 1) - 1
          // Deleted rather than left at zero: a kind nobody holds must not
          // draw a chip in the stack.
          if (left > 0) counts[post.viewer_reaction] = left
          else delete counts[post.viewer_reaction]
        }
        if (!clearing) counts[kind] = (counts[kind] ?? 0) + 1

        const total = Object.values(counts).reduce((sum, n) => sum + n, 0)
        return {
          ...post,
          liked_by_viewer: !clearing,
          viewer_reaction: clearing ? null : kind,
          reaction_counts: counts,
          reaction_total: total,
          like_count: total,
        }
      })

      return { previous }
    },

    onError: (_err, _vars, ctx) => {
      // Put back exactly what was there — not a re-derived guess.
      ctx?.previous?.forEach(([key, data]) => qc.setQueryData(key as string[], data))
    },

    onSuccess: (result, { postId }) => {
      // The server counted everybody's reaction; our guess counted one. Its
      // numbers win.
      patchPost(qc, postId, (post) => ({
        ...post,
        liked_by_viewer: result.liked,
        like_count: result.like_count,
        viewer_reaction: result.viewer_reaction,
        reaction_counts: result.reaction_counts,
        reaction_total: result.reaction_total,
      }))
    },
  })
}

/** Who reacted, with a tab per kind.
 *
 *  Fetched only when the list is actually opened — most people only ever look
 *  at the count. One query per kind, keyed by it, so switching tabs back does
 *  not re-ask.
 */
export function usePostReactions(
  postId: string,
  kind: string | null,
  enabled = true,
) {
  return useInfiniteQuery({
    queryKey: keys.postReactions(postId, kind),
    queryFn: ({ pageParam }) => {
      const params = new URLSearchParams({ limit: '20' })
      if (kind) params.set('kind', kind)
      if (pageParam) params.set('cursor', pageParam as string)
      return api.get<Page<Reactor>>(`/posts/${postId}/reactions?${params}`)
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    enabled,
  })
}

/** The reactions on offer. A fixed catalogue — cached forever, like feelings.
 *
 *  Served rather than hard-coded so web and mobile cannot drift on the wording.
 *  REACTIONS below is what the picker draws before this lands, so it never
 *  opens empty.
 */
export function useReactionKinds() {
  return useQuery({
    queryKey: keys.reactionKinds,
    queryFn: () => api.get<ReactionKindInfo[]>('/reactions'),
    staleTime: Infinity,
  })
}

/** Edit your own post. Author only — the server enforces it.
 *
 *  `media_ids` is the FULL list the post ends up with, not an addition, so
 *  removing a photo is possible at all; null leaves the photos alone.
 */
export function useEditPost() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      postId,
      ...input
    }: {
      postId: string
      body: string
      media_ids?: string[] | null
      feeling?: string | null
      /** "Remove the feeling" and "do not touch it" are both null otherwise. */
      clear_feeling?: boolean
    }) => api.patch<EditResult>(`/posts/${postId}`, input),
    // Refetched rather than patched from the response: the edit answers with
    // the body and edited_at only, and the card also needs the photos and the
    // feeling the server resolved.
    onSuccess: (_result, { postId }) => {
      qc.invalidateQueries({ queryKey: ['feed'] })
      qc.invalidateQueries({ queryKey: keys.post(postId) })
    },
  })
}

/* --------------------------------------------------------------- comments */

export function useComments(postId: string, enabled = true) {
  return useInfiniteQuery({
    queryKey: keys.comments(postId),
    queryFn: ({ pageParam }) => {
      const params = new URLSearchParams({ limit: '20' })
      if (pageParam) params.set('cursor', pageParam as string)
      return api.get<Page<Comment>>(`/posts/${postId}/comments?${params}`)
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    // Only fetch when the thread is actually open — otherwise every post on
    // screen would load its comments.
    enabled,
  })
}

export function useAddComment(postId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: string | { body: string; parent_id?: string | null }) =>
      api.post<CreateCommentResult>(
        `/posts/${postId}/comments`,
        // A bare string is still accepted so existing callers keep working;
        // a reply passes the parent.
        typeof input === 'string' ? { body: input } : input,
      ),
    onSuccess: (comment) => {
      // Comments read oldest-first, so a new one goes at the END of the last
      // page — the opposite of the feed.
      qc.setQueryData<InfiniteData<Page<Comment>>>(
        keys.comments(postId),
        (old) => {
          if (!old) return old
          const pages = [...old.pages]
          const lastIndex = pages.length - 1
          pages[lastIndex] = {
            ...pages[lastIndex],
            items: [...pages[lastIndex].items, comment],
          }
          return { ...old, pages }
        },
      )
      // The count on the post card comes from the server's recomputed value.
      qc.setQueriesData<FeedPages>({ queryKey: ['feed'] }, (old) => {
        if (!old) return old
        return {
          ...old,
          pages: old.pages.map((page) => ({
            ...page,
            items: page.items.map((post) =>
              post.id === postId
                ? { ...post, comment_count: comment.comment_count }
                : post,
            ),
          })),
        }
      })
    },
  })
}

/** Rewrite one comment in whichever thread holds it.
 *
 *  ONE HOOK FOR BOTH THREADS, matching the server: a comment id belongs to
 *  either a post's comments or a Prayer Request's, and neither the caller nor
 *  this cache should have to say which. A prefix match over both key families
 *  finds it wherever it is, and misses cost nothing.
 */
function patchComment(
  qc: ReturnType<typeof useQueryClient>,
  commentId: string,
  patch: <T extends { id: string }>(comment: T) => T,
) {
  for (const queryKey of [['comments'], ['prayer']]) {
    qc.setQueriesData<InfiniteData<Page<{ id: string }>>>({ queryKey }, (old) => {
      // Prayer keys also hold request LISTS, which have no items with comment
      // ids — the id match below simply never fires on those.
      if (!old?.pages) return old
      return {
        ...old,
        pages: old.pages.map((page) => ({
          ...page,
          items: page.items?.map((c) => (c.id === commentId ? patch(c) : c)),
        })),
      }
    })
  }
}

/** Like or unlike one comment, optimistically.
 *
 *  Deliberately silent on the server — nobody is notified their comment was
 *  liked — so there is nothing here to invalidate beyond the row itself.
 */
export function useToggleCommentLike() {
  const qc = useQueryClient()

  return useMutation({
    mutationFn: (commentId: string) =>
      api.post<LikeResult>(`/comments/${commentId}/like`),

    onMutate: async (commentId) => {
      await qc.cancelQueries({ queryKey: ['comments'] })
      const previous = [
        ...qc.getQueriesData({ queryKey: ['comments'] }),
        ...qc.getQueriesData({ queryKey: ['prayer'] }),
      ]

      patchComment(qc, commentId, (comment) => {
        const c = comment as typeof comment & {
          like_count: number
          liked_by_viewer: boolean
        }
        return {
          ...c,
          liked_by_viewer: !c.liked_by_viewer,
          // Floored at zero: a stale cache could otherwise render -1.
          like_count: Math.max(0, c.like_count + (c.liked_by_viewer ? -1 : 1)),
        }
      })

      return { previous }
    },

    onError: (_err, _id, ctx) => {
      ctx?.previous?.forEach(([key, data]) => qc.setQueryData(key, data))
    },

    onSuccess: (result, commentId) => {
      patchComment(qc, commentId, (comment) => ({
        ...comment,
        liked_by_viewer: result.liked,
        like_count: result.like_count,
      }))
    },
  })
}

/** Fix a typo in your own comment. Author only — the server enforces it.
 *
 *  The response carries the new body and edited_at, which is the whole of what
 *  changed, so the row is patched rather than the thread refetched.
 */
export function useEditComment() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ commentId, body }: { commentId: string; body: string }) =>
      api.patch<EditResult>(`/comments/${commentId}`, { body }),
    onSuccess: (result) => {
      patchComment(qc, result.id, (comment) => ({
        ...comment,
        body: result.body,
        edited_at: result.edited_at,
      }))
    },
  })
}

/* -------------------------------------------------------- prayer requests */

export function usePrayerRequests(tab: PrayerTab, state?: PrayerState | null) {
  return useInfiniteQuery({
    queryKey: keys.prayer(tab, state),
    queryFn: ({ pageParam }) => {
      const params = new URLSearchParams({ tab, limit: '20' })
      if (state) params.set('state', state)
      if (pageParam) params.set('cursor', pageParam as string)
      return api.get<Page<PrayerRequest>>(`/prayer-requests?${params}`)
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    staleTime: 30_000,
  })
}

export function useCreatePrayerRequest() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: {
      body: string
      state?: PrayerState
      visibility?: Visibility
      church_id?: string | null
      group_id?: string | null
      media_ids?: string[]
      /** The author's own tag: what practical help would meet this need.
       *  Optional — most requests are for prayer alone. */
      help_kind?: string | null
    }) => api.post<PrayerRequest>('/prayer-requests', input),
    onSuccess: () => {
      // Every tab can be affected — "All" and "My Requests" both gain it — so
      // this is one of the few places a broad invalidate is the right call.
      qc.invalidateQueries({ queryKey: ['prayer'] })
    },
  })
}

/** Respond to a Prayer Request: Comment · I Will Pray · I am Praying · Prayed.
 *
 *  Mutually exclusive per person, so the optimistic update REPLACES the
 *  previous response rather than adding to it. Sending the response that is
 *  already active clears it.
 */
export function useRespondToPrayer() {
  const qc = useQueryClient()

  return useMutation({
    mutationFn: ({
      id,
      response_type,
    }: {
      id: string
      response_type: ResponseType | null
    }) => api.post<RespondResult>(`/prayer-requests/${id}/respond`, { response_type }),

    onMutate: async ({ id, response_type }) => {
      await qc.cancelQueries({ queryKey: ['prayer'] })
      const previous = qc.getQueriesData<PrayerPages>({ queryKey: ['prayer'] })

      qc.setQueriesData<PrayerPages>({ queryKey: ['prayer'] }, (old) => {
        if (!old) return old
        return {
          ...old,
          pages: old.pages.map((page) => ({
            ...page,
            items: page.items.map((pr) => {
              if (pr.id !== id) return pr
              // "Responses" excludes plain comments — it counts people who
              // committed to pray, matching the server's response_count.
              const wasCommitted =
                pr.viewer_response !== null && pr.viewer_response !== 'comment'
              const nowCommitted =
                response_type !== null && response_type !== 'comment'
              const delta = Number(nowCommitted) - Number(wasCommitted)
              // Move the count off the old type and onto the new one, so
              // the dropdown is right before the server answers.
              const counts = { ...(pr.counts ?? {}) } as Record<string, number>
              if (pr.viewer_response) {
                counts[pr.viewer_response] = Math.max(
                  0,
                  (counts[pr.viewer_response] ?? 0) - 1,
                )
              }
              if (response_type) {
                counts[response_type] = (counts[response_type] ?? 0) + 1
              }

              return {
                ...pr,
                viewer_response: response_type,
                response_count: Math.max(0, pr.response_count + delta),
                counts: counts as PrayerRequest['counts'],
              }
            }),
          })),
        }
      })

      return { previous }
    },

    onError: (_err, _vars, ctx) => {
      ctx?.previous?.forEach(([key, data]) => qc.setQueryData(key, data))
    },

    onSuccess: (result, { id }) => {
      const committed =
        (result.counts.will_pray ?? 0) +
        (result.counts.praying ?? 0) +
        (result.counts.prayed ?? 0)

      qc.setQueriesData<PrayerPages>({ queryKey: ['prayer'] }, (old) => {
        if (!old) return old
        return {
          ...old,
          pages: old.pages.map((page) => ({
            ...page,
            items: page.items.map((pr) =>
              pr.id === id
                ? {
                    ...pr,
                    viewer_response: result.response_type,
                    response_count: committed,
                    comment_count: result.counts.comment ?? pr.comment_count,
                    // The per-type numbers shown IN the dropdown. Without
                    // this the old count survives a cleared response — tap to
                    // withdraw and "I am Praying 1" stays on screen.
                    counts: result.counts,
                  }
                : pr,
            ),
          })),
        }
      })

      // "My Prayers" gains or loses this request depending on the new
      // response, and that cannot be derived from the row alone.
      qc.invalidateQueries({ queryKey: keys.prayer('praying') })
    },
  })
}

/** Edit your own Prayer Request. Author only, and words only.
 *
 *  The sharpest case for the author-only rule: people commit to praying for
 *  what they read, and several may already have done so, so a request that
 *  quietly becomes a different request leaves those commitments attached to
 *  something nobody agreed to.
 */
export function useEditPrayerRequest() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: string }) =>
      api.patch<EditResult>(`/prayer-requests/${id}`, { body }),
    onSuccess: (result, { id }) => {
      const patch = (pr: PrayerRequest) => ({
        ...pr,
        body: result.body,
        edited_at: result.edited_at,
      })
      qc.setQueriesData<PrayerPages>({ queryKey: ['prayer'] }, (old) => {
        // The prayer keys also hold comment threads, whose items are comments
        // rather than requests; the id match never fires on those.
        if (!old?.pages) return old
        return {
          ...old,
          pages: old.pages.map((page) => ({
            ...page,
            items: page.items?.map((pr) => (pr.id === id ? patch(pr) : pr)),
          })),
        }
      })
      // The pinned copy a notification opened is its own cache entry.
      qc.setQueryData<PrayerRequest>(keys.prayerRequest(id), (old) =>
        old ? patch(old) : old,
      )
    },
  })
}

/** Move a Prayer Request between Red, Yellow and Green. Author only. */
export function useSetPrayerState() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, state }: { id: string; state: PrayerState }) =>
      api.patch<PrayerRequest>(`/prayer-requests/${id}/state`, { state }),
    onSuccess: (updated) => {
      qc.setQueriesData<PrayerPages>({ queryKey: ['prayer'] }, (old) => {
        if (!old) return old
        return {
          ...old,
          pages: old.pages.map((page) => ({
            ...page,
            items: page.items.map((pr) => (pr.id === updated.id ? updated : pr)),
          })),
        }
      })
    },
  })
}


/* -------------------------------------------------------- notifications */

/** The number on the bell.
 *
 *  WHY THIS POLLS AT ALL, GIVEN PUSH EXISTS.
 *
 *  Push is the better mechanism and, where it is available, this defers to it
 *  — see the interval below. But push cannot be the only mechanism on the web,
 *  for three separate reasons:
 *
 *    1. Permission is optional, and declining is common. Somebody who says no,
 *       or who never opens the bell to say yes, gets no push ever. For them the
 *       poll is not a fallback, it is the whole thing.
 *    2. iOS Safari only delivers web push to a site installed to the home
 *       screen. An ordinary tab on an iPhone receives nothing.
 *    3. PUSH CARRIES A MESSAGE, NOT A COUNT. It says something happened; the
 *       badge is a number that comes from the server. And the number also goes
 *       DOWN — reading a notification on the phone lowers it here, and no push
 *       is sent for that. Without a poll the web badge would drift out of step
 *       with the phone and stay wrong until a reload.
 *
 *  The third is the structural one: it would still be true if every user
 *  granted permission on every browser.
 *
 *  WHAT IT COSTS. One indexed COUNT over a single user's rows, only while a tab
 *  is open and in front of somebody. It stops when the tab is backgrounded.
 *
 *  THREE RATES, cheapest first:
 *
 *    push granted  → a minute. Push already refreshes this the moment anything
 *                    arrives (see onPushInForeground in AppShell), so the poll
 *                    is only catching what push structurally cannot: a count
 *                    that changed somewhere else.
 *    active        → ten seconds. A minute was the old rate, chosen assuming
 *                    nobody minds a slightly stale badge — wrong in the case
 *                    that matters most: two people in a room, one saying "I
 *                    have replied", the other watching a bell that stays empty.
 *    idle          → a minute. A tab left open all afternoon on a quiet
 *                    account; anything arriving, or the person touching the
 *                    page, snaps it back to ten seconds.
 */
const NOTIFY_POLL_FAST = 10_000
const NOTIFY_POLL_IDLE = 60_000
/** Quiet polls before backing off — two minutes of nothing at the fast rate. */
const NOTIFY_QUIET_LIMIT = 12

export function useUnreadCount() {
  const quiet = useRef(0)
  const last = useRef<number | null>(null)
  const qc = useQueryClient()

  // Somebody at the keyboard is somebody who might be waiting for something.
  useEffect(() => {
    const wake = () => {
      quiet.current = 0
    }
    window.addEventListener('focus', wake)
    document.addEventListener('visibilitychange', wake)
    return () => {
      window.removeEventListener('focus', wake)
      document.removeEventListener('visibilitychange', wake)
    }
  }, [])

  return useQuery({
    queryKey: keys.unreadCount,
    queryFn: async () => {
      const res = await api.get<UnreadCount>('/notifications/unread-count')
      // A changed count is activity, whichever way it moved — reading one is
      // as much a sign of somebody being here as receiving one.
      if (last.current !== null && res.unread_count !== last.current) {
        quiet.current = 0
        // A NEW NOTIFICATION MAY MEAN NEW ACCESS.
        //
        // Being approved into a group changes what you belong to, and the
        // approval happens on somebody ELSE's device — nothing on the member's
        // side knows to refetch, so their Groups list went on saying
        // "Requested" until the app was closed and reopened. Pull-to-refresh
        // did not help either: it refreshes the feed, not this.
        //
        // Only on an INCREASE. Reading a notification lowers the count and
        // changes nothing about membership.
        if (res.unread_count > (last.current ?? 0)) {
          qc.invalidateQueries({ queryKey: keys.myGroups })
        }
      } else {
        quiet.current += 1
      }
      last.current = res.unread_count
      return res
    },
    enabled: Boolean(tokens.access),
    refetchInterval: () => {
      // Read at each tick rather than once: somebody can grant permission from
      // the bell without reloading, and the poll should slow down immediately
      // rather than at the next refresh.
      if (pushGranted()) return NOTIFY_POLL_IDLE
      return quiet.current >= NOTIFY_QUIET_LIMIT
        ? NOTIFY_POLL_IDLE
        : NOTIFY_POLL_FAST
    },
    refetchIntervalInBackground: false,
    retry: false,
  })
}

/** One post, by id.
 *
 *  For arriving from a notification: the post it names may be pages down the
 *  feed or not loaded at all, so it is fetched rather than searched for. See
 *  FeedPage for what is done with it.
 */
export function usePost(postId: string | null) {
  return useQuery({
    queryKey: keys.post(postId ?? ''),
    queryFn: () => api.get<Post>(`/posts/${postId}`),
    enabled: Boolean(postId) && Boolean(tokens.access),
    retry: false,
  })
}

/** One Prayer Request, by id. Same reason as usePost. */
export function usePrayerRequest(id: string | null) {
  return useQuery({
    queryKey: keys.prayerRequest(id ?? ''),
    queryFn: () => api.get<PrayerRequest>(`/prayer-requests/${id}`),
    enabled: Boolean(id) && Boolean(tokens.access),
    retry: false,
  })
}

export function useNotifications(enabled = true) {
  return useInfiniteQuery({
    queryKey: keys.notifications,
    queryFn: ({ pageParam }) => {
      const params = new URLSearchParams({ limit: '20' })
      if (pageParam) params.set('cursor', pageParam as string)
      return api.get<NotificationsPage>(`/notifications?${params}`)
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    // Only fetched when the panel is actually open.
    enabled,
    staleTime: 15_000,
  })
}

/** Mark one read, optimistically.
 *
 *  Tapping a notification navigates immediately, so waiting for the server
 *  before clearing the highlight means it flickers back to unread as the new
 *  page renders.
 */
export function useMarkRead() {
  const qc = useQueryClient()

  return useMutation({
    mutationFn: (id: string) =>
      api.post<UnreadCount>(`/notifications/${id}/read`),

    onMutate: async (id) => {
      await qc.cancelQueries({ queryKey: keys.notifications })
      const previous = qc.getQueryData<InfiniteData<NotificationsPage>>(
        keys.notifications,
      )

      qc.setQueryData<InfiniteData<NotificationsPage>>(
        keys.notifications,
        (old) => {
          if (!old) return old
          return {
            ...old,
            pages: old.pages.map((page) => ({
              ...page,
              items: page.items.map((n) =>
                n.id === id ? { ...n, read: true } : n,
              ),
            })),
          }
        },
      )

      // Floored at zero: a stale cache could otherwise render -1 on the badge.
      qc.setQueryData<UnreadCount>(keys.unreadCount, (old) =>
        old ? { unread_count: Math.max(0, old.unread_count - 1) } : old,
      )

      return { previous }
    },

    onError: (_err, _id, ctx) => {
      if (ctx?.previous) qc.setQueryData(keys.notifications, ctx.previous)
      qc.invalidateQueries({ queryKey: keys.unreadCount })
    },

    onSuccess: (result) => {
      // The server's count wins — it knows about reads from other devices.
      qc.setQueryData<UnreadCount>(keys.unreadCount, result)
    },
  })
}

export function useMarkAllRead() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => api.post<{ marked: number }>('/notifications/read-all'),
    onSuccess: () => {
      qc.setQueryData<UnreadCount>(keys.unreadCount, { unread_count: 0 })
      qc.setQueryData<InfiniteData<NotificationsPage>>(
        keys.notifications,
        (old) => {
          if (!old) return old
          return {
            ...old,
            pages: old.pages.map((page) => ({
              ...page,
              unread_count: 0,
              items: page.items.map((n) => ({ ...n, read: true })),
            })),
          }
        },
      )
    },
  })
}


/* ---------------------------------------------------------------- groups */

export function useChurch(churchId?: string | null) {
  return useQuery({
    queryKey: keys.church(churchId ?? ''),
    queryFn: () => api.get<ChurchDetail>(`/groups/${churchId}`),
    enabled: Boolean(churchId),
  })
}

export function useChurchStats(churchId?: string | null) {
  return useQuery({
    queryKey: keys.churchStats(churchId ?? ''),
    queryFn: () => api.get<ChurchStats>(`/groups/${churchId}/stats`),
    enabled: Boolean(churchId),
    // Counted from source rows on every call, so worth a short cache — the
    // numbers do not move minute to minute.
    staleTime: 60_000,
  })
}

export function useMembers(churchId?: string | null) {
  return useQuery({
    queryKey: keys.members(churchId ?? ''),
    queryFn: () => items<Member>(`/groups/${churchId}/members`),
    enabled: Boolean(churchId),
  })
}

/** The groups the signed-in person belongs to.
 *
 *  The member's counterpart to useGroups. Every admin group route is
 *  permission-checked, so a plain member calling useGroups gets a 403 and an
 *  error card — this is the listing they are actually allowed to read, and it
 *  needs no church id because the server scopes it to their own memberships.
 */
/** What the signed-in person may DO in their church.
 *
 *  The server enforces these — see assert_permission — and this is the same
 *  set, so the UI can hide what would only 403. A control that appears and
 *  then fails is worse than one that was never offered: it reads as the app
 *  being broken rather than as something you are not allowed to do.
 *
 *  Church admins get the whole catalogue back, so no screen needs to special
 *  case them.
 */
export function useMyPermissions(churchId?: string | null, groupId?: string | null) {
  return useQuery({
    queryKey: [...keys.myPermissions(churchId ?? ''), groupId ?? 'church'],
    queryFn: () =>
      items<string>(
        // WITH the group when there is one. Authority can live on the group
        // row — a moderator of THIS group — and asking without it returned
        // only church-wide permissions, hiding buttons the server allows.
        `/groups/${churchId}/permissions${groupId ? `?group_id=${groupId}` : ''}`,
      ),
    enabled: Boolean(churchId),
    // Roles change rarely, and every screen asks — one fetch per session is
    // plenty. A promotion lands on the next reload.
    staleTime: 5 * 60_000,
  })
}

/** `can('events.create')` — reads better at the call site than an includes(). */
export function useCan(churchId?: string | null, groupId?: string | null) {
  const { data } = useMyPermissions(churchId, groupId)
  return (permission: string) => Boolean(data?.includes(permission))
}

export function useMyGroups(enabled = true) {
  return useQuery({
    queryKey: keys.myGroups,
    queryFn: () => api.get<MyGroupsPage>('/groups/my-groups'),
    enabled,
  })
}

/** Whether this person may take somebody else's post down.
 *
 *  REMOVING IS NOT EDITING, and the two have deliberately different rules.
 *  Editing is author-only, always: rewriting somebody's words publishes new
 *  ones under their name and face. Removing is moderation, reads as
 *  moderation, and belongs to whoever is responsible for the room:
 *
 *    super admin      — anywhere
 *    church admin     — anywhere in their church
 *    group moderator  — inside the group they moderate, and nowhere else
 *
 *  The same three the server allows (see can_moderate_post), so the menu
 *  cannot offer a Remove that would only 403.
 */
export function useCanModerate() {
  const { data: me } = useMe()
  const isChurchAdmin = me?.role === 'church_admin' || me?.role === 'super_admin'
  // Only asked for when it could matter — an admin already passes on the row
  // above, and a plain member's own groups are a single cached call.
  const myGroups = useMyGroups(Boolean(tokens.access) && !isChurchAdmin)

  return (groupId: string | null) => {
    if (isChurchAdmin) return true
    if (!groupId) return false
    return Boolean(
      myGroups.data?.items.find((g) => g.id === groupId)?.is_moderator,
    )
  }
}

/** Ask to join a group. Any member of the church may; the server refuses
 *  anyone already in it, and asking twice is a no-op. */
export function useRequestToJoin() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ churchId, groupId }: { churchId: string; groupId: string }) =>
      api.post(`/groups/${churchId}/groups/${groupId}/join-requests`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.myGroups }),
  })
}

/** The pending queue for a group — admins and that group's moderators only. */
export function useJoinRequests(churchId?: string | null, groupId?: string | null) {
  return useQuery({
    queryKey: keys.joinRequests(churchId ?? '', groupId ?? ''),
    queryFn: () =>
      items<JoinRequestPerson>(
        `/groups/${churchId}/groups/${groupId}/join-requests`,
      ),
    enabled: Boolean(churchId && groupId),
  })
}

export function useAnswerJoinRequest(churchId: string, groupId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ requestId, approve }: { requestId: string; approve: boolean }) =>
      api.post(
        `/groups/${churchId}/groups/${groupId}/join-requests/${requestId}`,
        { approve },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.joinRequests(churchId, groupId) })
      // Approving adds a member, so the group's member list is stale too.
      qc.invalidateQueries({ queryKey: keys.groupMembers(churchId, groupId) })
    },
  })
}

export function useGroups(churchId?: string | null) {
  return useQuery({
    queryKey: keys.groups(churchId ?? ''),
    queryFn: () => items<Group>(`/groups/${churchId}/groups`),
    enabled: Boolean(churchId),
  })
}

/** One group, for its detail page. */
export function useGroup(churchId?: string | null, groupId?: string | null) {
  return useQuery({
    queryKey: keys.group(churchId ?? '', groupId ?? ''),
    queryFn: () =>
      api.get<GroupDetail>(`/groups/${churchId}/groups/${groupId}`),
    enabled: Boolean(churchId && groupId),
  })
}

/** The people in one group. */
export function useGroupMembers(
  churchId?: string | null,
  groupId?: string | null,
) {
  return useQuery({
    queryKey: keys.groupMembers(churchId ?? '', groupId ?? ''),
    queryFn: () =>
      items<Member>(`/groups/${churchId}/groups/${groupId}/members`),
    enabled: Boolean(churchId && groupId),
  })
}

/** A group's own wall. Members only — the API 403s otherwise. */
export function useGroupFeed(groupId?: string | null) {
  return useInfiniteQuery({
    queryKey: keys.groupFeed(groupId ?? ''),
    queryFn: ({ pageParam }) => {
      const params = new URLSearchParams({ limit: '20', group_id: groupId! })
      if (pageParam) params.set('cursor', pageParam as string)
      return api.get<Page<Post>>(`/feed?${params}`)
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    enabled: Boolean(groupId),
    staleTime: 30_000,
  })
}

/* ------------------------------------------------------ group management */

export function useUpdateGroup(churchId: string, groupId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: { name?: string; description?: string | null }) =>
      api.patch<{ ok: boolean }>(
        `/groups/${churchId}/groups/${groupId}`,
        input,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.group(churchId, groupId) })
      qc.invalidateQueries({ queryKey: keys.groups(churchId) })
    },
  })
}

export function useDeleteGroup(churchId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (groupId: string) =>
      api.delete<{ ok: boolean }>(`/groups/${churchId}/groups/${groupId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.groups(churchId) })
      qc.invalidateQueries({ queryKey: keys.churchStats(churchId) })
    },
  })
}

/** Promote or demote within ONE group — not church-wide. See migration 0013. */
export function useSetGroupModerator(churchId: string, groupId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ userId, isModerator }: { userId: string; isModerator: boolean }) =>
      api.post<{ ok: boolean }>(
        `/groups/${churchId}/groups/${groupId}/moderators`,
        { user_id: userId, is_moderator: isModerator },
      ),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: keys.groupMembers(churchId, groupId) }),
  })
}

/* ----------------------------------------------------------- events */

export function useGroupEvents(churchId?: string | null, groupId?: string | null) {
  return useQuery({
    queryKey: keys.groupEvents(churchId ?? '', groupId ?? ''),
    queryFn: () =>
      items<GroupEvent>(`/groups/${churchId}/groups/${groupId}/events`),
    enabled: Boolean(churchId && groupId),
  })
}

export function useCreateEvent(churchId: string, groupId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: {
      title: string
      starts_at: string
      description?: string | null
      location?: string | null
      ends_at?: string | null
    }) =>
      api.post<{ id: string }>(
        `/groups/${churchId}/groups/${groupId}/events`,
        input,
      ),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: keys.groupEvents(churchId, groupId) }),
  })
}

export function useRsvp(churchId: string, groupId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ eventId, status }: { eventId: string; status: RsvpStatus }) =>
      api.post<{ going: number; maybe: number; not_going: number }>(
        `/groups/${churchId}/groups/${groupId}/events/${eventId}/rsvp`,
        { status },
      ),
    // Refetched rather than patched: the counts AND my_rsvp both move, and the
    // list is small enough that a round trip is cheaper than getting it wrong.
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: keys.groupEvents(churchId, groupId) }),
  })
}

/** Who answered, and how. Fetched only when the list is opened — most people
 *  only ever look at the counts. */
export function useRsvps(
  churchId: string,
  groupId: string,
  eventId?: string | null,
) {
  return useQuery({
    queryKey: ['church', churchId, 'groups', groupId, 'events', eventId, 'rsvps'],
    queryFn: () =>
      items<RsvpPerson>(
        `/groups/${churchId}/groups/${groupId}/events/${eventId}/rsvps`,
      ),
    enabled: Boolean(eventId),
  })
}

export function useDeleteEvent(churchId: string, groupId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (eventId: string) =>
      api.delete<{ ok: boolean }>(
        `/groups/${churchId}/groups/${groupId}/events/${eventId}`,
      ),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: keys.groupEvents(churchId, groupId) }),
  })
}

/* ------------------------------------------------------------ polls */

export function useGroupPolls(churchId?: string | null, groupId?: string | null) {
  return useQuery({
    queryKey: keys.groupPolls(churchId ?? '', groupId ?? ''),
    queryFn: () =>
      items<Poll>(`/groups/${churchId}/groups/${groupId}/polls`),
    enabled: Boolean(churchId && groupId),
  })
}

export function useCreatePoll(churchId: string, groupId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: {
      question: string
      options: string[]
      closes_at?: string | null
    }) =>
      api.post<{ id: string }>(
        `/groups/${churchId}/groups/${groupId}/polls`,
        input,
      ),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: keys.groupPolls(churchId, groupId) }),
  })
}

export function useVote(churchId: string, groupId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ pollId, optionId }: { pollId: string; optionId: string }) =>
      api.post<PollTally>(
        `/groups/${churchId}/groups/${groupId}/polls/${pollId}/vote`,
        { option_id: optionId },
      ),
    // The server returns the whole tally, so the cached poll is patched with
    // real numbers rather than refetched.
    onSuccess: (tally, { pollId }) => {
      qc.setQueryData<Poll[]>(keys.groupPolls(churchId, groupId), (old) =>
        old?.map((p) =>
          p.id === pollId
            ? {
                ...p,
                options: tally.options,
                total_votes: tally.total_votes,
                my_option_id: tally.my_option_id,
              }
            : p,
        ),
      )
    },
  })
}

/** Who voted for what. */
export function useVoters(
  churchId: string,
  groupId: string,
  pollId?: string | null,
) {
  return useQuery({
    queryKey: ['church', churchId, 'groups', groupId, 'polls', pollId, 'voters'],
    queryFn: () =>
      items<Voter>(
        `/groups/${churchId}/groups/${groupId}/polls/${pollId}/voters`,
      ),
    enabled: Boolean(pollId),
  })
}

export function useDeletePoll(churchId: string, groupId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (pollId: string) =>
      api.delete<{ ok: boolean }>(
        `/groups/${churchId}/groups/${groupId}/polls/${pollId}`,
      ),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: keys.groupPolls(churchId, groupId) }),
  })
}

/* ------------------------------------------------------ permissions */

/** Every permission a role may carry, grouped and in plain words.
 *
 *  Served by the API rather than hard-coded here so this screen and the
 *  validator in create_role can never disagree about what exists. */
export function usePermissionCatalogue() {
  return useQuery({
    queryKey: keys.permissionCatalogue,
    queryFn: () => api.get<PermissionGroup[]>('/groups/permission-catalogue'),
    staleTime: Infinity,
  })
}

/** Give somebody one of the church's roles, in this group.
 *
 *  The general form of the moderator toggle — an admin who built "Youth Lead"
 *  can hand it out. The server derives is_moderator from whether the role can
 *  actually moderate, so the badge always matches the abilities.
 */
export function useSetGroupMemberRole(churchId: string, groupId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ userId, roleId }: { userId: string; roleId: string | null }) =>
      api.post(
        `/groups/${churchId}/groups/${groupId}/members/${userId}/role`,
        { role_id: roleId },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.groupMembers(churchId, groupId) })
      // Their own permissions change with the role, so anything gated on them
      // must re-ask.
      qc.invalidateQueries({ queryKey: keys.myPermissions(churchId) })
    },
  })
}

export function useRoles(churchId?: string | null) {
  return useQuery({
    queryKey: keys.roles(churchId ?? ''),
    queryFn: () => items<ChurchRole>(`/groups/${churchId}/roles`),
    enabled: Boolean(churchId),
  })
}

export function useCreateRole(churchId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: {
      name: string
      description?: string | null
      permissions: string[]
    }) => api.post<{ id: string }>(`/groups/${churchId}/roles`, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.roles(churchId) })
      qc.invalidateQueries({ queryKey: keys.churchStats(churchId) })
    },
  })
}

export function useUpdateRole(churchId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      roleId,
      ...input
    }: {
      roleId: string
      name?: string
      description?: string | null
      permissions?: string[]
    }) => api.patch<{ ok: boolean }>(`/groups/${churchId}/roles/${roleId}`, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.roles(churchId) })
      // A rename changes the badge on every moderator carrying it.
      qc.invalidateQueries({ queryKey: keys.members(churchId) })
    },
  })
}

export function useDeleteRole(churchId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (roleId: string) =>
      api.delete<{ ok: boolean }>(`/groups/${churchId}/roles/${roleId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.roles(churchId) })
      // Everyone who carried it now shows as a moderator with no named role.
      qc.invalidateQueries({ queryKey: keys.members(churchId) })
      qc.invalidateQueries({ queryKey: keys.churchStats(churchId) })
    },
  })
}

/* ------------------------------------------------------------- invitations */

export function useGroupInvitations(
  churchId?: string | null,
  groupId?: string | null,
) {
  return useQuery({
    queryKey: keys.groupInvitations(churchId ?? '', groupId ?? ''),
    queryFn: () =>
      items<Invitation>(`/groups/${churchId}/groups/${groupId}/invitations`),
    enabled: Boolean(churchId && groupId),
  })
}

/** Invite one person or a pasted list — the same call either way. */
export function useInviteToGroup(churchId: string, groupId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (entries: { mobile: string; name?: string | null }[]) =>
      api.post<InviteResult>(
        `/groups/${churchId}/groups/${groupId}/invitations`,
        { entries },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.groupInvitations(churchId, groupId) })
      // Someone already registered may have accepted instantly elsewhere; the
      // member list and counts are both cheap to refetch.
      qc.invalidateQueries({ queryKey: keys.groupMembers(churchId, groupId) })
      qc.invalidateQueries({ queryKey: keys.group(churchId, groupId) })
    },
  })
}

export function useRevokeInvitation(churchId: string, groupId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (invitationId: string) =>
      api.delete<{ ok: boolean }>(
        `/groups/${churchId}/groups/${groupId}/invitations/${invitationId}`,
      ),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: keys.groupInvitations(churchId, groupId) }),
  })
}

/** Invitations waiting for the signed-in person. */
export function useMyInvitations() {
  return useQuery({
    queryKey: keys.myInvitations,
    queryFn: () => items<MyInvitation>('/groups/my-invitations'),
    staleTime: 60_000,
  })
}

/** A church created for YOU that nobody administers yet. Null is normal. */
export function useChurchAwaitingMe() {
  return useQuery({
    queryKey: ['churchAwaitingMe'] as const,
    queryFn: () =>
      api.get<ChurchAwaitingMe | null>('/groups/awaiting-me'),
    staleTime: 60_000,
  })
}

/** Claim it. Queues an approval request; the Super Admin decides. */
export function useClaimChurch() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (slug: string) =>
      api.post<{ id: string }>('/groups/register-admin', { slug }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['churchAwaitingMe'] })
    },
  })
}

export function useRespondToInvitation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, accept }: { id: string; accept: boolean }) =>
      api.post<{ ok: boolean }>(`/groups/my-invitations/${id}`, { accept }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.myInvitations })
      // Accepting adds a membership, which changes what the groups list and
      // the feed may show.
      qc.invalidateQueries({ queryKey: ['church'] })
      qc.invalidateQueries({ queryKey: ['feed'] })
    },
  })
}

export function useCreateGroup(churchId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: { name: string; description?: string | null }) =>
      api.post<{ id: string }>(`/groups/${churchId}/groups`, input),
    onSuccess: () => {
      // The list AND the stats row both change, and neither can be derived
      // from the response alone.
      qc.invalidateQueries({ queryKey: keys.groups(churchId) })
      qc.invalidateQueries({ queryKey: keys.churchStats(churchId) })
    },
  })
}

/** Promote or demote a moderator.
 *
 *  Moderator is a SUB-GROUP role, not a church-wide one — see
 *  docs/POST_SIGNUP_JOURNEY.md. The endpoint takes the member and the flag.
 */
export function useSetModerator(churchId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      userId,
      isModerator,
      roleId,
    }: {
      userId: string
      isModerator: boolean
      /** The named role they carry, e.g. "Moderator-1". Omitted means no
       *  named role — the server clears it on a demotion either way. */
      roleId?: string | null
    }) =>
      api.post<{ ok: boolean }>(`/groups/${churchId}/moderators`, {
        user_id: userId,
        is_moderator: isModerator,
        role_id: roleId ?? null,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.members(churchId) })
      qc.invalidateQueries({ queryKey: keys.churchStats(churchId) })
      // EVERY group's member list too, not just the church roster.
      //
      // is_moderator lives on the church membership row, so promoting someone
      // changes their badge on every group page they appear on — and those
      // read keys.groupMembers, a different cache entry. Without this the
      // badge only corrected itself when the tab was remounted, which read as
      // the promotion silently failing.
      //
      // A prefix match rather than one key: the promoted person may be in
      // several groups, and we do not know which are cached.
      qc.invalidateQueries({ queryKey: ['church', churchId, 'groups'] })
    },
  })
}


/* ----------------------------------------------------------------- media */

/** Upload a photo and get back its id and dimensions.
 *
 *  Runs while the composer is still open, so pressing Post is instant rather
 *  than a wait. Ids are handed to the create call on publish.
 */
export function useUploadImage() {
  return useMutation({
    mutationFn: (file: File) => uploadFile<Media>('/media', file),
  })
}

/** Upload a PDF — an order of service, a printable notice.
 *
 *  The same endpoint, told which kind it is getting. The checking behind it
 *  is completely different: an image is decoded and rebuilt, a PDF cannot be.
 */
export function useUploadDocument() {
  return useMutation({
    mutationFn: (file: File) =>
      uploadFile<Media>('/media', file, { kind: 'document' }),
  })
}


/* ------------------------------------------------- prayer: follow and help */

/** The client's ten options. Static in practice, so cached hard. */
export function useHelpOptions() {
  return useQuery({
    queryKey: keys.helpOptions,
    queryFn: () => api.get<HelpOption[]>('/prayer-requests/help/options'),
    staleTime: Infinity,
  })
}

/** The feelings a post can carry. A fixed catalogue — cached forever. */
export function useFeelings() {
  return useQuery({
    queryKey: ['feelings'] as const,
    queryFn: () => api.get<Feeling[]>('/feelings'),
    staleTime: Infinity,
  })
}

export function useMyOffers() {
  return useQuery({
    queryKey: keys.myOffers,
    queryFn: () => api.get<{ kinds: string[] }>('/prayer-requests/help/offers'),
    enabled: Boolean(tokens.access),
  })
}

/** The badge on the Help chip.
 *
 *  Polled like the notification bell and for the same reason: a number that
 *  changes a few times a day does not justify a socket. */
export function useHelpUnseen() {
  return useQuery({
    queryKey: ['help', 'unseen'],
    queryFn: () =>
      api.get<{ unseen_count: number }>('/prayer-requests/help/unseen-count'),
    enabled: Boolean(tokens.access),
    refetchInterval: 60_000,
    refetchIntervalInBackground: false,
    retry: false,
  })
}

/** Opening the dialog clears the badge. */
export function useMarkHelpSeen() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => api.post('/prayer-requests/help/seen'),
    onSuccess: () => {
      // Written straight in rather than invalidated: the badge should drop the
      // moment the dialog opens, not after a round trip.
      qc.setQueryData(['help', 'unseen'], { unseen_count: 0 })
    },
  })
}

export function useSetOffers() {
  const qc = useQueryClient()
  return useMutation({
    // PUT, because it is the whole set — the popup shows every option with the
    // current ones ticked, so unticking has to mean something.
    mutationFn: (kinds: string[]) =>
      api.put<{ kinds: string[] }>('/prayer-requests/help/offers', { kinds }),
    onSuccess: (result) => {
      qc.setQueryData(keys.myOffers, result)
      qc.invalidateQueries({ queryKey: ['help', 'matches'] })
    },
  })
}

export function useHelpMatches(kinds: string[], enabled = true) {
  return useInfiniteQuery({
    queryKey: keys.helpMatches(kinds),
    queryFn: ({ pageParam }) => {
      const params = new URLSearchParams({ kinds: kinds.join(','), limit: '20' })
      if (pageParam) params.set('cursor', pageParam as string)
      return api.get<Page<PrayerRequest>>(
        `/prayer-requests/help/matches?${params}`,
      )
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    enabled: enabled && kinds.length > 0,
  })
}

export function useFollowing() {
  return useQuery({
    queryKey: keys.following,
    queryFn: () => api.get<{ following: boolean }>('/prayer-requests/follow'),
    enabled: Boolean(tokens.access),
  })
}

export function useSetFollowing() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (following: boolean) =>
      api.put<{ following: boolean }>('/prayer-requests/follow', { following }),
    // Optimistic: a toggle that waits for a round trip feels broken.
    onMutate: async (following) => {
      await qc.cancelQueries({ queryKey: keys.following })
      const previous = qc.getQueryData<{ following: boolean }>(keys.following)
      qc.setQueryData(keys.following, { following })
      return { previous }
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.previous) qc.setQueryData(keys.following, ctx.previous)
    },
    onSuccess: (result) => qc.setQueryData(keys.following, result),
  })
}

/* ------------------------------------------------- prayer request comments */

export function usePrayerComments(id: string, enabled = true) {
  return useInfiniteQuery({
    queryKey: keys.prayerComments(id),
    queryFn: ({ pageParam }) => {
      const params = new URLSearchParams({ limit: '20' })
      if (pageParam) params.set('cursor', pageParam as string)
      return api.get<Page<PrayerComment>>(
        `/prayer-requests/${id}/comments?${params}`,
      )
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    // Only when the thread is open — otherwise every card on screen fetches.
    enabled,
  })
}

export function useAddPrayerComment(prayerRequestId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: {
      body: string
      help_kind?: string | null
      parent_id?: string | null
    }) =>
      api.post<PrayerComment>(
        `/prayer-requests/${prayerRequestId}/comments`,
        input,
      ),
    onSuccess: (comment) => {
      // Comments read oldest-first, so a new one goes at the END.
      qc.setQueryData<InfiniteData<Page<PrayerComment>>>(
        keys.prayerComments(prayerRequestId),
        (old) => {
          if (!old) return old
          const pages = [...old.pages]
          const last = pages.length - 1
          pages[last] = { ...pages[last], items: [...pages[last].items, comment] }
          return { ...old, pages }
        },
      )
      // The comment count on the card comes from the server.
      qc.invalidateQueries({ queryKey: ['prayer'] })
    },
  })
}


/** Follow or unfollow ONE Prayer Request — the card control.
 *
 *  Optimistic, because a toggle that waits for a round trip feels broken.
 */
export function useSetRequestFollowing() {
  const qc = useQueryClient()

  return useMutation({
    mutationFn: ({ id, following }: { id: string; following: boolean }) =>
      api.put<{ following: boolean }>(`/prayer-requests/${id}/follow`, {
        following,
      }),

    onMutate: async ({ id, following }) => {
      await qc.cancelQueries({ queryKey: ['prayer'] })
      const previous = qc.getQueriesData<PrayerPages>({ queryKey: ['prayer'] })

      qc.setQueriesData<PrayerPages>({ queryKey: ['prayer'] }, (old) => {
        if (!old) return old
        return {
          ...old,
          pages: old.pages.map((page) => ({
            ...page,
            items: page.items.map((pr) =>
              pr.id === id ? { ...pr, following } : pr,
            ),
          })),
        }
      })

      return { previous }
    },

    onError: (_e, _v, ctx) => {
      ctx?.previous?.forEach(([key, data]) => qc.setQueryData(key, data))
    },

    onSuccess: () => {
      // The followed LIST changes, and that cannot be derived from the row.
      qc.invalidateQueries({ queryKey: keys.followedRequests })
    },
  })
}

/** What this person follows — the list behind the header chip. */
export function useFollowedRequests(enabled = true) {
  return useInfiniteQuery({
    queryKey: keys.followedRequests,
    queryFn: ({ pageParam }) => {
      const params = new URLSearchParams({ limit: '20' })
      if (pageParam) params.set('cursor', pageParam as string)
      return api.get<Page<PrayerRequest>>(`/prayer-requests/following?${params}`)
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    enabled,
  })
}

/* ---------------------------------------------------------------- messaging
 *
 * Bulk messaging: a church admin writes once and the congregation hears it,
 * through the bell and on their phones.
 */

export function useMessages(churchId?: string | null) {
  return useQuery({
    queryKey: keys.messages(churchId ?? ''),
    queryFn: () => api.get<Page<ChurchMessage>>(
      `/churches/${churchId}/messages?limit=30`,
    ),
    enabled: Boolean(churchId),
  })
}

export function useSendMessage(churchId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: {
      title: string
      body: string
      audience_kind: AudienceKind
      group_id?: string | null
      role_id?: string | null
      user_ids?: string[]
      audience_filter?: AudienceFilter | null
      channels: { in_app: boolean; push: boolean; sms: boolean }
      /** Ids from POST /media, uploaded while the composer was open. */
      media_ids?: string[]
    }) => api.post<ChurchMessage>(`/churches/${churchId}/messages`, input),
    onSuccess: () => {
      /* Refetched rather than prepended: the server sends it in the
         background, so the row that matters is the one it wrote — including
         the recipient count, which the client cannot know. */
      qc.invalidateQueries({ queryKey: keys.messages(churchId) })
    },
  })
}

/** Polled while a send is in flight, so the admin watches it finish. */
export function useMessage(
  churchId: string | null | undefined,
  messageId: string | null,
  { poll = false }: { poll?: boolean } = {},
) {
  return useQuery({
    queryKey: keys.message(churchId ?? '', messageId ?? ''),
    queryFn: () => api.get<ChurchMessage>(
      `/churches/${churchId}/messages/${messageId}`,
    ),
    enabled: Boolean(churchId && messageId),
    /* Two seconds while sending, then stop. A church of three thousand takes
       a few seconds to fan out; anything longer is a failure the row itself
       will describe. */
    refetchInterval: poll ? 2000 : false,
  })
}

export function useChannelPreferences() {
  return useQuery({
    queryKey: keys.channelPreferences,
    queryFn: () => api.get<ChannelPreferences>('/me/channel-preferences'),
  })
}

export function useSetChannelPreferences() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: Partial<ChannelPreferences>) =>
      api.patch<ChannelPreferences>('/me/channel-preferences', input),
    onSuccess: (prefs) => {
      qc.setQueryData(keys.channelPreferences, prefs)
    },
  })
}

/** The Logs tab. Infinite, because a large church is thousands of rows. */
export function useMessageRecipients(
  churchId: string | null | undefined,
  messageId: string | null,
  filters: { channel?: string; status?: string } = {},
) {
  const key = `${filters.channel ?? 'all'}:${filters.status ?? 'all'}`
  return useInfiniteQuery({
    queryKey: keys.messageRecipients(churchId ?? '', messageId ?? '', key),
    queryFn: ({ pageParam }) => {
      const params = new URLSearchParams({ limit: '50' })
      if (pageParam) params.set('cursor', pageParam as string)
      if (filters.channel) params.set('channel', filters.channel)
      if (filters.status) params.set('status', filters.status)
      return api.get<Page<MessageRecipient>>(
        `/churches/${churchId}/messages/${messageId}/recipients?${params}`,
      )
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    enabled: Boolean(churchId && messageId),
  })
}

export function useMessageAnalytics(
  churchId: string | null | undefined,
  messageId: string | null,
) {
  return useQuery({
    queryKey: keys.messageAnalytics(churchId ?? '', messageId ?? ''),
    queryFn: () => api.get<MessageAnalytics>(
      `/churches/${churchId}/messages/${messageId}/analytics`,
    ),
    enabled: Boolean(churchId && messageId),
  })
}

/** How reachable this church is — independent of any one message. */
export function useMessagingAnalytics(churchId?: string | null) {
  return useQuery({
    queryKey: keys.messagingAnalytics(churchId ?? ''),
    queryFn: () => api.get<ChurchMessagingAnalytics>(
      `/churches/${churchId}/messaging-analytics`,
    ),
    enabled: Boolean(churchId),
  })
}

/* ---------------------------------------------------------------- devotions */

export function useDevotions(churchId?: string | null) {
  return useQuery({
    queryKey: keys.devotions(churchId ?? ''),
    queryFn: () => items<Devotion>(`/churches/${churchId}/devotions`),
    enabled: Boolean(churchId),
  })
}

export function useCreateDevotion(churchId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: {
      title: string
      body: string
      send_time: string
      days: number
      audience_kind: AudienceKind
      group_id?: string | null
      role_id?: string | null
      channels: { in_app: boolean; push: boolean; sms: boolean }
      /** Attached once and sent every morning. */
      media_ids?: string[]
    }) => api.post<Devotion>(`/churches/${churchId}/devotions`, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.devotions(churchId) })
    },
  })
}

/* ----------------------------------------------------------------- calls */

/** The calls landing page — "Scheduled/upcoming calls" and "Prev conf calls".
 *
 *  One hook for both tabs, because they are the same query with the clock
 *  pointing the other way.
 */
export function useCalls(
  churchId: string | null,
  upcoming = true,
  groupId?: string | null,
) {
  return useQuery({
    queryKey: keys.calls(churchId ?? '', upcoming, groupId),
    queryFn: () =>
      items<ScheduledCall>(
        `/groups/${churchId}/calls?upcoming=${upcoming}` +
          (groupId ? `&group_id=${groupId}` : ''),
      ),
    enabled: Boolean(churchId),
  })
}

/** Schedule one. `kind` is which of the three buttons was pressed. */
/** Who came, who spoke, who never arrived. */
export function useCallAttendance(
  churchId: string | null,
  callId: string | null,
  params: { came?: boolean; q?: string; page?: number; pageSize?: number } = {},
  enabled = true,
) {
  const { came, q = '', page = 1, pageSize = 25 } = params
  return useQuery({
    queryKey: [
      'church', churchId ?? '', 'calls', callId ?? '', 'attendance',
      came ?? 'all', q, page, pageSize,
    ] as const,
    queryFn: () => {
      const s = new URLSearchParams()
      if (came !== undefined) s.set('came', String(came))
      if (q) s.set('q', q)
      s.set('page', String(page))
      s.set('page_size', String(pageSize))
      return api.get<CallAttendancePage>(
        `/groups/${churchId}/calls/${callId}/attendance?${s}`,
      )
    },
    enabled: enabled && Boolean(churchId && callId),
    // The table keeps the last page on screen while the next one arrives.
    placeholderData: (prev) => prev,
  })
}

/** Finish the call for everybody, and close the recording. */
export function useEndCall(callId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => api.post<ScheduledCall>(`/me/calls/${callId}/end`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['me', 'calls'] })
      qc.invalidateQueries({ queryKey: ['church'] })
    },
  })
}

/** How a call did — reach now, turnout once a room can be joined. */
export function useCallAnalytics(
  churchId: string | null,
  callId: string | null,
) {
  return useQuery({
    queryKey: keys.callAnalytics(churchId ?? '', callId ?? ''),
    queryFn: () =>
      api.get<CallAnalytics>(
        `/groups/${churchId}/calls/${callId}/analytics`,
      ),
    enabled: Boolean(churchId && callId),
  })
}

/** Calls this person is invited to.
 *
 *  A different question from the church's schedule, and a different
 *  permission: being in the audience is the whole of it. No church id — the
 *  server knows which church they are in.
 */
export function useMyCalls(enabled = true) {
  return useQuery({
    queryKey: ['me', 'calls'] as const,
    queryFn: () => items<ScheduledCall>('/me/calls'),
    enabled,
  })
}

/* ------------------------------------------------------- inside a room */

/** The room, polled while somebody is in it.
 *
 *  POLLED RATHER THAN PUSHED, for now. A live connection is the right answer
 *  and arrives with the media platform's signalling — until then a few
 *  seconds late is the honest behaviour, and the hand queue is the only
 *  thing where that is even noticeable.
 */
export function useRoom(
  callId: string | null,
  poll = true,
  filters?: { roleId?: string | null; q?: string },
) {
  /* The filters narrow the ROSTER only — the floor and the queue are what a
     moderator is acting on and must not vanish because somebody typed into
     a search box. The server enforces that; this just carries it. */
  const params = new URLSearchParams()
  if (filters?.roleId) params.set('role_id', filters.roleId)
  if (filters?.q?.trim()) params.set('q', filters.q.trim())
  const qs = params.toString()

  return useQuery({
    queryKey: ['me', 'calls', callId, 'room', qs] as const,
    queryFn: () =>
      api.get<RoomState>(`/me/calls/${callId}/room${qs ? `?${qs}` : ''}`),
    enabled: Boolean(callId),
    /* Not once the room answers "not found": the call is over, and a
       screen left open on it would otherwise ask every few seconds for
       as long as the tab stays open. */
    refetchInterval: (query) =>
      poll &&
      !(query.state.error instanceof ApiError && query.state.error.status === 404)
        ? 4000
        : false,
    /* THE POLL IS THE SIGN OF LIFE. The server takes somebody whose screen
       stops asking to have left, so a listener who switches to another tab
       must keep asking — by default this stops the moment the tab is
       hidden, which would remove everybody who looked away. */
    refetchIntervalInBackground: poll,
    /* Keep the previous room on screen while a filter change is in flight.
       A roster that blanks between keystrokes is unusable. */
    placeholderData: (prev) => prev,
  })
}

/** Stop speaking, without waiting to be stopped. */
export function useStepDown(callId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => api.post<RoomState>(`/me/calls/${callId}/step-down`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['me', 'calls', callId, 'room'] })
    },
  })
}

/** Return the room to one voice. Leaves the chairperson speaking. */
export function useMuteEveryone(callId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () =>
      api.post<RoomState>(`/me/calls/${callId}/mute-everyone`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['me', 'calls', callId, 'room'] })
    },
  })
}

/** Put a room the server just returned on screen at once.
 *
 *  THE ROOM IS CACHED PER ROSTER FILTER — the key ends in the filter's
 *  query string, empty when there is none. Writing to the key without it
 *  wrote to an entry nothing reads, so every action in the room (a hand
 *  going up, somebody being allowed to speak) showed only on the next
 *  poll, seconds later, and read as the button not having worked.
 *
 *  The answer is unfiltered, so it fills the unfiltered entry; any
 *  filtered roster is refetched rather than overwritten with the wrong
 *  list. */
function showRoom(
  qc: ReturnType<typeof useQueryClient>,
  callId: string,
  state: RoomState,
) {
  qc.setQueryData(['me', 'calls', callId, 'room', ''], state)
  qc.invalidateQueries({ queryKey: ['me', 'calls', callId, 'room'] })
}

export function useJoinRoom(callId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => api.post<RoomState>(`/me/calls/${callId}/join`),
    onSuccess: (state) => {
      showRoom(qc, callId, state)
    },
  })
}

export function useLeaveRoom(callId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => api.post<void>(`/me/calls/${callId}/leave`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['me', 'calls'] })
    },
  })
}

export function useRaiseHand(callId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (up: boolean) =>
      api.post<RoomState>(`/me/calls/${callId}/hand`, { up }),
    onSuccess: (state) => {
      showRoom(qc, callId, state)
    },
  })
}

/** Give somebody the floor, or take it back. Moderator only. */
export function useSetCallRole(callId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: { user_id: string; role: 'listener' | 'speaker' }) =>
      api.post<RoomState>(`/me/calls/${callId}/role`, input),
    onSuccess: (state) => {
      showRoom(qc, callId, state)
    },
  })
}

export function useSetCallMuted(callId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: { user_id: string; muted: boolean }) =>
      api.post<RoomState>(`/me/calls/${callId}/mute`, input),
    onSuccess: (state) => {
      showRoom(qc, callId, state)
    },
  })
}

export function useScheduleCall(churchId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: {
      kind: CallKind
      title: string
      description?: string | null
      /** Who it is for — the same four a bulk message offers. */
      audience_kind?: AudienceKind
      group_id?: string | null
      role_id?: string | null
      user_ids?: string[]
      audience_filter?: AudienceFilter | null
      /** Null is "Now" — the client's own form has it as a radio. */
      starts_at?: string | null
      is_public?: boolean
      recording?: boolean
      moderator_id?: string | null
      moderated_by?: 'all_admins' | 'creator' | null
      host_video?: boolean | null
      participant_video?: boolean | null
      host_mic?: boolean | null
      participant_mic?: boolean | null
      max_participants?: number | null
      allow_chat?: boolean | null
      allow_comments?: boolean | null
    }) =>
      api.post<ScheduledCall>(`/groups/${churchId}/calls`, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['church', churchId, 'calls'] })
      /* The person who arranged it is in the audience of most calls they
         make, so their own upcoming list has changed too. */
      qc.invalidateQueries({ queryKey: ['me', 'calls'] })
    },
  })
}

export function useCancelCall(churchId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (callId: string) =>
      api.delete<void>(`/groups/${churchId}/calls/${callId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['church', churchId, 'calls'] })
      qc.invalidateQueries({ queryKey: ['me', 'calls'] })
    },
  })
}

/* ------------------------------------------------------------- templates */

/** The registered sentences that can go out over SMS or WhatsApp.
 *
 *  SHARED, not per church — the church's own name is filled in as a variable,
 *  which is what lets one carrier approval serve everybody. A handful of
 *  rows, so cached hard and never paged.
 */
export function useMessageTemplates(channel?: 'sms' | 'whatsapp') {
  return useQuery({
    queryKey: keys.messageTemplates(channel),
    queryFn: () =>
      items<MessageTemplate>(
        channel ? `/message-templates?channel=${channel}` : '/message-templates',
      ),
    staleTime: 5 * 60 * 1000,
  })
}

/** Exactly what this church's members would receive.
 *
 *  Church-scoped because the church's own name is one of the values — the
 *  same template is not the same sentence for two different churches.
 */
export function usePreviewTemplate(churchId: string) {
  return useMutation({
    mutationFn: ({
      templateId,
      values,
    }: {
      templateId: string
      values: Record<string, string>
    }) =>
      api.post<TemplatePreview>(
        `/churches/${churchId}/message-templates/${templateId}/preview`,
        { values },
      ),
  })
}

export function useSetDevotionActive(churchId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      api.patch<Devotion>(`/churches/${churchId}/devotions/${id}`, {
        is_active: active,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.devotions(churchId) })
    },
  })
}

export function useDeleteDevotion(churchId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) =>
      api.delete<void>(`/churches/${churchId}/devotions/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.devotions(churchId) })
      /* The plan is gone but what it sent stays — the history changed too. */
      qc.invalidateQueries({ queryKey: keys.messages(churchId) })
    },
  })
}


/* ------------------------------------------------------------ live events */

/** A live event, polled. The poll is what counts somebody as watching, and
 *  what tells the page the host has gone on air or ended. */
export function useLive(callId: string | null) {
  return useQuery({
    queryKey: ['live', callId] as const,
    queryFn: () => api.get<LiveEvent>(`/live/${callId}`),
    enabled: Boolean(callId),
    /* Until there is nothing left to learn. An ended live still has news
       coming — the replay is made after the host presses End, so right
       after ending there is none yet, then one being prepared, then one
       ready. Stopping at "ended" missed all three. */
    refetchInterval: (query) => {
      const live = query.state.data
      if (live?.state !== 'ended') return 4000
      if (live.replay?.status === 'ready' || live.replay?.status === 'failed') {
        return false
      }
      // Nothing was broadcast, so no replay is coming.
      const ended = live.ended_at ? new Date(live.ended_at).getTime() : 0
      return Date.now() - ended > 10 * 60_000 ? false : 4000
    },
    refetchIntervalInBackground: true,
  })
}

export function useEndLive(callId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => api.post<LiveEvent>(`/live/${callId}/end`),
    onSuccess: (live) => qc.setQueryData(['live', callId], live),
  })
}


/* ---------------------------------------------------- choosing by rules */

/** Gender, Age, and this church's own signup questions. */
export function useAudienceFields(churchId: string | null) {
  return useQuery({
    queryKey: ['church', churchId ?? '', 'audience', 'fields'] as const,
    queryFn: () =>
      items<AudienceField>(`/groups/${churchId}/audience/fields`),
    enabled: Boolean(churchId),
    staleTime: 60_000,
  })
}

/** How many people an audience reaches right now.
 *
 *  Asked as the rules are edited; the caller debounces. Only complete
 *  rules are sent, so a half-typed one does not turn the count into an
 *  error. */
export function useAudiencePreview(
  churchId: string | null,
  input: {
    audience_kind: 'filter' | 'church' | 'group' | 'group_role'
    group_id?: string | null
    role_id?: string | null
    audience_filter?: AudienceFilter | null
  },
  enabled = true,
) {
  return useQuery({
    queryKey: ['church', churchId ?? '', 'audience', 'preview', input] as const,
    queryFn: () =>
      api.post<AudiencePreview>(`/groups/${churchId}/audience/preview`, input),
    enabled: enabled && Boolean(churchId),
    placeholderData: (prev) => prev,
    retry: false,
  })
}

/** The Members table: a search, optional rules, one numbered page. */
export function useMemberTable(
  churchId: string | null,
  input: {
    q?: string
    group_id?: string | null
    audience_filter?: AudienceFilter | null
    page: number
    page_size?: number
  },
) {
  return useQuery({
    queryKey: [...keys.members(churchId ?? ''), 'table', input] as const,
    queryFn: () =>
      api.post<MemberTable>(`/groups/${churchId}/members/query`, input),
    enabled: Boolean(churchId),
    placeholderData: (prev) => prev,
    retry: false,
  })
}


/* ------------------------------------------------------------- attendance
 *
 * Who came on Sunday. Everything below is keyed under the church, so one
 * invalidation of ['church', cid, 'attendance'] refreshes every screen that
 * shows a count after a check-in or a mark.
 */

const att = (cid: string) => ['church', cid, 'attendance'] as const

export function useChurchPlace(churchId?: string | null) {
  return useQuery({
    queryKey: [...att(churchId ?? ''), 'place'] as const,
    queryFn: () => api.get<ChurchPlace>(`/groups/${churchId}/attendance/place`),
    enabled: Boolean(churchId),
  })
}

export function useSetChurchPlace(churchId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: {
      latitude: number | null
      longitude: number | null
      radius_m: number
    }) => api.put<ChurchPlace>(`/groups/${churchId}/attendance/place`, input),
    onSuccess: (place) => {
      qc.setQueryData([...att(churchId), 'place'], place)
      qc.invalidateQueries({ queryKey: [...att(churchId), 'today'] })
    },
  })
}

export function useServices(churchId?: string | null) {
  return useQuery({
    queryKey: [...att(churchId ?? ''), 'services'] as const,
    queryFn: () => items<Service>(`/groups/${churchId}/attendance/services`),
    enabled: Boolean(churchId),
  })
}

export function useSaveService(churchId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, input }: { id?: string; input: ServiceInput }) =>
      id
        ? api.patch<unknown>(`/groups/${churchId}/attendance/services/${id}`, input)
        : api.post<unknown>(`/groups/${churchId}/attendance/services`, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: att(churchId) }),
  })
}

export function useDeleteService(churchId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) =>
      api.delete<void>(`/groups/${churchId}/attendance/services/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: att(churchId) }),
  })
}

/** "Take attendance" on one group event. */
export function useSetEventAttendance(churchId: string, groupId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ eventId, enabled }: { eventId: string; enabled: boolean }) =>
      api.put<{ take_attendance: boolean; service_id: string | null }>(
        `/groups/${churchId}/attendance/events/${eventId}`,
        { enabled },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.groupEvents(churchId, groupId) })
      qc.invalidateQueries({ queryKey: att(churchId) })
    },
  })
}

/** The member's day: what is on, whether they are in, and their household.
 *
 *  Refetched on focus with no stale time — somebody opens the app in the
 *  car park, and the banner has to be right that minute. */
export function useAttendanceToday(churchId?: string | null) {
  return useQuery({
    queryKey: [...att(churchId ?? ''), 'today'] as const,
    queryFn: () => api.get<AttendanceToday>(`/groups/${churchId}/attendance/today`),
    enabled: Boolean(churchId),
    refetchOnWindowFocus: 'always',
    staleTime: 0,
    retry: false,
  })
}

export function useCheckIn(churchId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      occurrenceId,
      ...input
    }: {
      occurrenceId: string
      latitude: number
      longitude: number
      accuracy_m: number | null
      is_mock: boolean
      people?: string[]
    }) =>
      api.post<CheckedIn>(
        `/groups/${churchId}/attendance/occurrences/${occurrenceId}/check-in`,
        input,
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: att(churchId) }),
  })
}

export function useUndoCheckIn(churchId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (occurrenceId: string) =>
      api.delete<void>(
        `/groups/${churchId}/attendance/occurrences/${occurrenceId}/check-in`,
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: att(churchId) }),
  })
}

export function useAttendanceWeek(churchId?: string | null) {
  return useQuery({
    queryKey: [...att(churchId ?? ''), 'week'] as const,
    queryFn: () => items<WeekItem>(`/groups/${churchId}/attendance/week`),
    enabled: Boolean(churchId),
  })
}

/** "I'll be away" for one day, or back after all. */
export function useSetAway(churchId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ date, away }: { date: string; away: boolean }) =>
      away
        ? api.post<unknown>(`/groups/${churchId}/attendance/away`, {
            starts_on: date,
          })
        : api.delete<unknown>(
            `/groups/${churchId}/attendance/away?on=${encodeURIComponent(date)}`,
          ),
    onSuccess: () => qc.invalidateQueries({ queryKey: att(churchId) }),
  })
}

/** "Attend regularly" on a service members choose. */
export function useSetRegular(churchId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ serviceId, regular }: { serviceId: string; regular: boolean }) =>
      api.put<{ regular: boolean }>(
        `/groups/${churchId}/attendance/services/${serviceId}/regular`,
        { regular },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: att(churchId) }),
  })
}

export function useMyAttendance(churchId?: string | null) {
  return useQuery({
    queryKey: [...att(churchId ?? ''), 'mine'] as const,
    queryFn: () =>
      api.get<MyAttendance>(`/groups/${churchId}/attendance/mine?limit=12`),
    enabled: Boolean(churchId),
  })
}

/** Service days, newest first. */
export function useOccurrences(
  churchId: string | null | undefined,
  input: { page?: number; pageSize?: number; serviceId?: string | null } = {},
  enabled = true,
) {
  const params = new URLSearchParams({
    page: String(input.page ?? 1),
    page_size: String(input.pageSize ?? 20),
  })
  if (input.serviceId) params.set('service_id', input.serviceId)
  return useQuery({
    queryKey: [...att(churchId ?? ''), 'occurrences', params.toString()] as const,
    queryFn: () =>
      api.get<NumberedPage<Occurrence>>(
        `/groups/${churchId}/attendance/occurrences?${params}`,
      ),
    enabled: enabled && Boolean(churchId),
    placeholderData: (prev) => prev,
  })
}

export function useRoll(
  churchId: string | null | undefined,
  occurrenceId: string | null | undefined,
  input: { q?: string; show?: 'all' | 'present' | 'not_marked'; page: number; pageSize?: number },
) {
  const params = new URLSearchParams({
    page: String(input.page),
    page_size: String(input.pageSize ?? 25),
    show: input.show ?? 'all',
  })
  if (input.q) params.set('q', input.q)
  return useQuery({
    queryKey: [...att(churchId ?? ''), 'roll', occurrenceId, params.toString()] as const,
    queryFn: () =>
      api.get<Roll>(
        `/groups/${churchId}/attendance/occurrences/${occurrenceId}/roll?${params}`,
      ),
    enabled: Boolean(churchId && occurrenceId),
    placeholderData: (prev) => prev,
  })
}

export function useMark(churchId: string, occurrenceId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      userId,
      present,
      slotId,
    }: {
      userId: string
      present: boolean
      /** Omitted means whichever time is on now. */
      slotId?: string | null
    }) =>
      present
        ? api.post<unknown>(
            `/groups/${churchId}/attendance/occurrences/${occurrenceId}/marks`,
            { user_ids: [userId], ...(slotId ? { slot_id: slotId } : {}) },
          )
        : api.delete<unknown>(
            `/groups/${churchId}/attendance/occurrences/${occurrenceId}/marks/${userId}`,
          ),
    onSuccess: () => qc.invalidateQueries({ queryKey: att(churchId) }),
  })
}

export function useOccurrenceReport(
  churchId: string | null | undefined,
  occurrenceId: string | null | undefined,
) {
  return useQuery({
    queryKey: [...att(churchId ?? ''), 'report', occurrenceId] as const,
    queryFn: () =>
      api.get<OccurrenceReport>(
        `/groups/${churchId}/attendance/occurrences/${occurrenceId}`,
      ),
    enabled: Boolean(churchId && occurrenceId),
  })
}

export function useFollowUp(
  churchId: string | null | undefined,
  occurrenceId: string | null | undefined,
  input: { kind: FollowUpKind; q?: string; page: number; pageSize?: number },
  enabled = true,
) {
  const params = new URLSearchParams({
    kind: input.kind,
    page: String(input.page),
    page_size: String(input.pageSize ?? 25),
  })
  if (input.q) params.set('q', input.q)
  return useQuery({
    queryKey: [...att(churchId ?? ''), 'follow-up', occurrenceId, params.toString()] as const,
    queryFn: () =>
      api.get<FollowUpPage>(
        `/groups/${churchId}/attendance/occurrences/${occurrenceId}/follow-up?${params}`,
      ),
    enabled: enabled && Boolean(churchId && occurrenceId),
    placeholderData: (prev) => prev,
  })
}

/** Every id on one follow-up list, for a message or a meeting. Asked at the
 *  moment of the click, so it is never a stale list. */
export function fetchFollowUpIds(
  churchId: string,
  occurrenceId: string,
  kind: FollowUpKind,
): Promise<string[]> {
  return api
    .get<{ ids: string[] }>(
      `/groups/${churchId}/attendance/occurrences/${occurrenceId}/follow-up/ids?kind=${kind}`,
    )
    .then((r) => r.ids)
}

export function useExcuse(churchId: string, occurrenceId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ userId, reason }: { userId: string; reason: string | null | false }) =>
      reason === false
        ? api.delete<unknown>(
            `/groups/${churchId}/attendance/occurrences/${occurrenceId}/excuses/${userId}`,
          )
        : api.put<unknown>(
            `/groups/${churchId}/attendance/occurrences/${occurrenceId}/excuses/${userId}`,
            { reason },
          ),
    onSuccess: () => qc.invalidateQueries({ queryKey: att(churchId) }),
  })
}

/* ------------------------------------------------------------- households */

const householdKey = (headId: string | null) =>
  ['household', headId ?? 'me'] as const

/** My family, or — for an administrator — somebody else's.
 *  `churchId` and `headId` together mean another person's household. */
export function useHousehold(churchId?: string | null, headId?: string | null) {
  return useQuery({
    queryKey: householdKey(headId ?? null),
    queryFn: () =>
      api.get<Household>(
        headId
          ? `/groups/${churchId}/attendance/households/${headId}`
          : '/me/household',
      ),
    enabled: headId ? Boolean(churchId) : true,
  })
}

export function useSaveHouseholdMember(churchId?: string | null, headId?: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, input }: { id?: string; input: HouseholdMemberInput }) =>
      id
        ? api.patch<unknown>(`/me/household/${id}`, input)
        : api.post<unknown>(
            headId
              ? `/groups/${churchId}/attendance/households/${headId}`
              : '/me/household',
            input,
          ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: householdKey(headId ?? null) })
      if (churchId) {
        qc.invalidateQueries({ queryKey: att(churchId) })
        qc.invalidateQueries({ queryKey: keys.members(churchId) })
      }
    },
  })
}

export function useRemoveHouseholdMember() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.delete<void>(`/me/household/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: householdKey(null) })
      qc.invalidateQueries({ queryKey: ['church'] })
    },
  })
}
